import type {
  PollMutation,
  PollsAPIResponse,
  PollsMetaData,
  TransformedPollsResponse,
  TransformedVotesResponse,
  VotesAPIResponse,
  VotesAPIResponseIncluded,
  VotesMetaData,
  VotesResponse,
  WinnerMutation
} from '@library/types/polls';
import {
  normalizePollsResponse,
  normalizeVotesResponse
} from '@bo-utils/polls';
import { api } from '@bo-services/api';

export const pollsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPolls: build.query<
      TransformedPollsResponse,
      { page: number; date?: string }
    >({
      query: ({ page, date }) => ({
        url: '/polls-api/admin/polls',
        params: {
          page,
          ...(date && { 'q[date_eq]': date })
        }
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.polls.map(({ id }) => ({
                type: 'Polls' as const,
                id
              })),
              'Polls'
            ]
          : ['Polls'],
      transformResponse: (response: {
        data: PollsAPIResponse[];
        included: VotesResponse[];
        meta: PollsMetaData;
      }): TransformedPollsResponse => normalizePollsResponse(response)
    }),
    createPoll: build.mutation<void, FormData>({
      query: (formData) => ({
        url: `/polls-api/admin/polls`,
        method: 'POST',
        body: formData
      }),
      invalidatesTags: ['Polls']
    }),
    editPoll: build.mutation<void, PollMutation>({
      query: ({ formData, pollId }) => ({
        url: `/polls-api/admin/polls/${pollId}`,
        method: 'PATCH',
        body: formData
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Polls', id: arg.pollId }
      ]
    }),
    setPollWinner: build.mutation<void, WinnerMutation>({
      query: (optionId) => ({
        url: `/polls-api/admin/options/${optionId}/winner`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      invalidatesTags: ['Polls']
    }),
    getVotes: build.query<
      TransformedVotesResponse,
      { page: number; uuid: string }
    >({
      query: ({ uuid, page }) => ({
        url: '/polls-api/admin/votes',
        params: {
          page,
          'q[user_id_eq]': uuid
        }
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Votes', id: arg.uuid }],
      transformResponse: (response: {
        data: VotesAPIResponse[];
        included: VotesAPIResponseIncluded;
        meta: VotesMetaData;
      }): TransformedVotesResponse => normalizeVotesResponse(response)
    })
  })
});

export const {
  useGetPollsQuery,
  useCreatePollMutation,
  useEditPollMutation,
  useSetPollWinnerMutation,
  useGetVotesQuery
} = pollsApi;
